import { useEffect, useMemo } from 'react';
import JsonLd from '../../components/feature/JsonLd';
import Hero from './components/Hero';
import Services from './components/Services';
import AICapabilities from './components/AICapabilities';
import Process from './components/Process';
import CTA from './components/CTA';
import Blog from './components/Blog';

export default function HomePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    document.title = 'AI-Powered BPO Nearshore Call Center Solutions | Call Center Pros – Boca Raton, FL';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Call Center Pros delivers AI-powered BPO nearshore solutions with native English-speaking agents in Boca Raton, FL. Scalable call center outsourcing, AI automation, and customer service excellence.');
  }, []);

  const websiteSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Call Center Pros',
    url: siteUrl,
    description: 'AI-powered BPO nearshore call center solutions with native English-speaking agents.',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/bpo-blog?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  }), [siteUrl]);

  const organizationSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Call Center Pros',
    url: siteUrl,
    logo: `${siteUrl}/vite.svg`,
    description: 'AI-powered BPO outsourcing and nearshore call center solutions provider since 2012.',
    foundingDate: '2012',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '4400 N Federal Hwy, Suite 401',
      addressLocality: 'Boca Raton',
      addressRegion: 'FL',
      postalCode: '33431',
      addressCountry: 'US',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'info@callcenterpros.com',
      contactType: 'customer service',
      availableLanguage: ['English', 'Spanish'],
    },
    sameAs: [
      'https://m.facebook.com/CallCenterPros',
      'https://twitter.com/callcenter_pros',
      'https://www.instagram.com/ccprostt',
      'https://www.linkedin.com/company/call-center-pros',
    ],
  }), [siteUrl]);

  const localBusinessSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Call Center Pros',
    image: `${siteUrl}/vite.svg`,
    url: siteUrl,
    telephone: '',
    email: 'info@callcenterpros.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '4400 N Federal Hwy, Suite 401',
      addressLocality: 'Boca Raton',
      addressRegion: 'FL',
      postalCode: '33431',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 26.3683,
      longitude: -80.0775,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
    ],
    priceRange: '$$',
    description: 'AI-powered BPO nearshore call center solutions with dedicated native English-speaking agents and advanced AI automation technology.',
  }), [siteUrl]);

  return (
    <div className="min-h-screen bg-white">
      <JsonLd id="home-website-schema" data={websiteSchema} />
      <JsonLd id="home-org-schema" data={organizationSchema} />
      <JsonLd id="home-localbusiness-schema" data={localBusinessSchema} />
      <Hero />
      <Services />
      <AICapabilities />
      <Process />
      <CTA />
      <Blog />
    </div>
  );
}
